import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import {
  useApproveB2BInvoice,
  useB2BInvoice,
  useDeleteB2BInvoice,
  useMarkBookedB2BInvoice,
  useMarkPaidB2BInvoice,
  useMarkTransferredB2BInvoice,
  useRejectB2BInvoice,
  useSubmitB2BInvoice,
  useUpdateB2BInvoice,
} from "../hooks/useB2BInvoices";
import { B2BInvoiceStatusBadge } from "../components/B2BInvoiceStatusBadge";
import { B2BInvoiceForm } from "../components/B2BInvoiceForm";
import { B2BInvoiceDiscussion } from "../components/B2BInvoiceDiscussion";
import { StatusTimeline } from "../components/StatusTimeline";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";

function formatPln(value: string | null): string {
  if (value === null) return "—";
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return new Intl.NumberFormat("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    // "always" forces a thousands separator for 4-digit pl-PL values; the pl-PL
    // CLDR default (minimumGroupingDigits=2) otherwise skips grouping below 10 000.
    useGrouping: "always",
  }).format(n);
}

export default function B2BInvoiceDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { data: inv, isLoading } = useB2BInvoice(id);
  usePageTitle(inv && `Faktura B2B ${inv.invoice_number || "(szkic)"}`);
  // Powiadomienie o oznaczeniu w dyskusji prowadzi wprost do niej.
  const { ref: discussionRef } = useConversationDeepLink<HTMLDivElement>(!isLoading && !!inv);

  const update = useUpdateB2BInvoice(id);
  const remove = useDeleteB2BInvoice();
  const submit = useSubmitB2BInvoice();
  const approve = useApproveB2BInvoice();
  const reject = useRejectB2BInvoice();
  const markBooked = useMarkBookedB2BInvoice();
  const markTransferred = useMarkTransferredB2BInvoice();
  const markPaid = useMarkPaidB2BInvoice();

  const [editing, setEditing] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (isLoading) return <Spinner />;
  if (!inv) return <p>Nie znaleziono faktury.</p>;

  const isOwner = inv.employee === user?.id;
  const isCEO = user?.is_ceo === true;
  const isAccountant = user?.role === "accountant";

  const canEdit = isOwner && (inv.status === "draft" || inv.status === "rejected");
  const canDelete = canEdit;
  const canSubmit = isOwner && (inv.status === "draft" || inv.status === "rejected") && inv.invoice_managed_file !== null;
  const canApproveReject = isCEO && inv.status === "submitted";
  const canMarkBooked = (isCEO || isAccountant) && inv.status === "approved";
  const canMarkTransferred = (isCEO || isAccountant) && inv.status === "booked";
  const canMarkPaid = (isCEO || isAccountant) && inv.status === "transferred_for_payment";
  // Dyskusja — ten sam krąg co dostęp do faktury (backend: user_can_access_b2b_discussion).
  const canDiscuss = isOwner || isCEO || isAccountant;

  if (editing && canEdit) {
    return (
      <div className="max-w-2xl space-y-4">
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> Anuluj edycję
        </button>
        <h1 className="text-2xl font-semibold">Edycja faktury B2B</h1>
        <B2BInvoiceForm
          invoice={inv}
          isSubmitting={update.isPending}
          onSubmit={async (data) => {
            await update.mutateAsync(data);
            setEditing(false);
          }}
          submitLabel="Zapisz zmiany"
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-5">
      <Link to="/b2b-invoices" className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="mr-1 h-4 w-4" /> Powrót do listy
      </Link>

      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Faktura {inv.invoice_number}</h1>
          <p className="text-sm text-slate-600">
            Okres: {String(inv.period_month).padStart(2, "0")}.{inv.period_year} •{" "}
            {inv.employee_full_name}
            {inv.employee_is_vat_payer && (
              <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800">
                VATowiec
              </span>
            )}
          </p>
        </div>
        <B2BInvoiceStatusBadge status={inv.status} />
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-slate-200 bg-white p-4 text-sm">
        <div>
          <dt className="text-slate-500">Data wystawienia</dt>
          <dd className="font-medium">{formatDate(inv.issue_date)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Stawka VAT</dt>
          <dd className="font-medium">
            {inv.employee_vat_rate_display ?? <span className="text-slate-400">brak</span>}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Kwota brutto</dt>
          <dd className="font-semibold">{formatPln(inv.amount_gross)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">
            Kwota netto <span className="text-xs text-slate-400 font-normal">(obliczona)</span>
          </dt>
          <dd className="font-medium">
            {(() => {
              const gross = Number(inv.amount_gross);
              const rate = inv.employee_vat_rate;
              if (!Number.isFinite(gross) || rate === null || rate === undefined) {
                return <span className="text-slate-400">—</span>;
              }
              const pct = rate === "zw" ? 0 : Number(rate);
              const net = gross / (1 + pct / 100);
              return formatPln(net.toFixed(2));
            })()}
          </dd>
        </div>
        {inv.salary_baseline_snapshot !== null && (
          <div>
            <dt className="text-slate-500">Koszt pracy w momencie wysłania</dt>
            <dd>{formatPln(inv.salary_baseline_snapshot)}</dd>
          </div>
        )}
        <div>
          <dt className="text-slate-500">Plik faktury</dt>
          <dd>
            {inv.invoice_file ? (
              <a
                href={inv.invoice_file_url ?? "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sky-700 underline"
              >
                {inv.invoice_file.original_filename}
              </a>
            ) : (
              <span className="text-slate-400">brak</span>
            )}
          </dd>
        </div>
        {inv.notes && (
          <div className="col-span-2">
            <dt className="text-slate-500">Uwagi</dt>
            <dd className="whitespace-pre-line">{inv.notes}</dd>
          </div>
        )}
      </dl>

      {inv.rejection_reason && (
        <div className="rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">
          <strong>Powód odrzucenia:</strong> {inv.rejection_reason}
        </div>
      )}

      <section className="rounded-md border border-slate-200 bg-white p-4 text-sm">
        <h2 className="mb-3 font-medium">Historia statusów</h2>
        <StatusTimeline inv={inv} />
      </section>

      <div className="flex flex-wrap gap-2">
        {canEdit && (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edytuj
          </Button>
        )}
        {canSubmit && (
          <Button
            onClick={() => submit.mutate({ id })}
            disabled={submit.isPending}
          >
            Wyślij do zatwierdzenia
          </Button>
        )}
        {canApproveReject && (
          <>
            <Button
              onClick={() => approve.mutate({ id })}
              disabled={approve.isPending}
            >
              Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setRejectOpen(true)}>
              Odrzuć
            </Button>
          </>
        )}
        {canMarkBooked && (
          <Button
            onClick={() => markBooked.mutate({ id })}
            disabled={markBooked.isPending}
          >
            Oznacz jako zaksięgowaną
          </Button>
        )}
        {canMarkTransferred && (
          <Button
            onClick={() => markTransferred.mutate({ id })}
            disabled={markTransferred.isPending}
          >
            Oznacz jako przekazaną do opłacenia
          </Button>
        )}
        {canMarkPaid && (
          <Button
            onClick={() => markPaid.mutate({ id })}
            disabled={markPaid.isPending}
          >
            Oznacz jako opłaconą
          </Button>
        )}
        {canDelete && (
          <Button
            variant="secondary"
            onClick={async () => {
              if (!confirm("Czy na pewno usunąć tę fakturę?")) return;
              await remove.mutateAsync(id);
              navigate("/b2b-invoices");
            }}
          >
            <Trash2 className="mr-1 h-4 w-4" /> Usuń
          </Button>
        )}
      </div>

      {canDiscuss && (
        <div ref={discussionRef} className="scroll-mt-4">
          <B2BInvoiceDiscussion invoiceId={inv.id} employeeId={inv.employee} />
        </div>
      )}

      <Modal open={rejectOpen} onClose={() => setRejectOpen(false)} title="Odrzuć fakturę">
        <div className="space-y-3">
          <label className="block text-sm font-medium text-slate-700">
            Powód odrzucenia
          </label>
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            rows={4}
            className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            placeholder="Wyjaśnij dlaczego faktura nie może być zatwierdzona…"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setRejectOpen(false)}>
              Anuluj
            </Button>
            <Button
              onClick={async () => {
                if (rejectReason.trim().length < 3) {
                  alert("Powód odrzucenia musi mieć co najmniej 3 znaki.");
                  return;
                }
                await reject.mutateAsync({ id, rejection_reason: rejectReason });
                setRejectOpen(false);
                setRejectReason("");
              }}
              disabled={reject.isPending}
            >
              Odrzuć
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
